// ML001-F0218 / F0222 / F0223 (WebUI #169), moved to the generic relay by WebUI #172 (M004-F0742). The credit-note client, isolated in
// this one module. Every operation is sent through Core's Service Link Pass-through (CSLPT), the generic relay:
// POST /api/v1/orgs/:orgId/modules/ledger/messages with the closed envelope { relay_version: 1, type, payload, target_user_id? }.
// Core reads `type` and `target_user_id` (the customer the frame names) and forwards `payload` unread to the Ledger as the frame body.
//
// Customer rules (Core !1034, the cslptManifest rows CN_READ / CN_WRITE / CN_DRAFT):
//   - WRITES (draft, approve, cancel, discard) ALWAYS send target_user_id = the customer. Without it Core takes the caller as the
//     customer and refuses a mismatch. Draft also sets payload.user_id to that same customer (Core refuses a mismatch).
//     This client refuses to send a write with no customer (no request is made).
//   - READS: list sends target_user_id only when it filters by a customer (user_id); get and pdf name no customer.
//
// No per-operation switch exists (superseded: the generic path is the only path for these operations).
// Frame bodies (payload) are the ones Core's gateway builds for the typed routes: approve { credit_note_id },
// cancel { credit_note_id, reason }, discard { credit_note_id, reason? }, get/pdf { credit_note_id }.
//
// ANSWERS keep the shape the components read: Core answers { success, data: { ..., payload } } and `payload` is the Ledger's tunnel
// payload { binding_generation, orgId, organisationUuid, data }. This client returns { ...response, data: { success: true, data: <Ledger data> } }
// so `response.data.data` is the Ledger's data, as the typed routes answered.
//
// History is NOT read here: it opens the shared TX history drawer with the note's history_key (owner 2026-10-10: one history method).
import api from './api';

const messagesPath = (orgId) => `/v1/orgs/${encodeURIComponent(orgId)}/modules/ledger/messages`;
// target_user_id is a member of the envelope only when a customer is named (JSON drops undefined; the key is never sent empty).
const envelope = (type, payload, targetUserId) => ({
  relay_version: 1, type, payload, ...(targetUserId === undefined ? {} : { target_user_id: targetUserId }),
});
const keyed = (idempotencyKey) => ({ headers: { 'Idempotency-Key': idempotencyKey } });

// A write names its customer. Refused here, before any request, when the customer is missing or not a positive integer.
const customerOf = (value) => {
  const id = typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : value;
  if (!Number.isSafeInteger(id) || id < 1) throw new Error('A customer is required for this credit-note operation');
  return id;
};

// The typed-route answer shape: { ...axios response, data: { success: true, data: <Ledger data> } }. Errors never reach this function
// (they are thrown by the transport and carry Core's error envelope unchanged).
const typedAnswer = (response) => ({
  ...response,
  data: { success: true, data: response?.data?.data?.payload?.data },
});

export const creditNoteApi = {
  // Reads: a customer is named only when the list is filtered by one (user_id).
  list: async (orgId, params = {}) => typedAnswer(await api.post(
    messagesPath(orgId),
    envelope('credit_note.list', params, params.user_id === undefined ? undefined : customerOf(params.user_id)),
  )),
  get: async (orgId, creditNoteId) => typedAnswer(await api.post(
    messagesPath(orgId),
    envelope('credit_note.get', { credit_note_id: creditNoteId }),
  )),
  pdf: async (orgId, creditNoteId) => typedAnswer(await api.post(
    messagesPath(orgId),
    envelope('credit_note.pdf', { credit_note_id: creditNoteId }),
  )),
  // Writes: the customer is the draft's user_id, sent as the target and as the payload member (Core compares them).
  createDraft: async (orgId, body, idempotencyKey) => {
    const customer = customerOf(body?.user_id);
    return typedAnswer(await api.post(
      messagesPath(orgId),
      envelope('credit_note.draft', { ...body, user_id: customer }, customer),
      keyed(idempotencyKey),
    ));
  },
  approve: async (orgId, creditNoteId, idempotencyKey, customerUserId) => typedAnswer(await api.post(
    messagesPath(orgId),
    envelope('credit_note.approve', { credit_note_id: creditNoteId }, customerOf(customerUserId)),
    keyed(idempotencyKey),
  )),
  cancel: async (orgId, creditNoteId, body, idempotencyKey, customerUserId) => typedAnswer(await api.post(
    messagesPath(orgId),
    envelope('credit_note.cancel', { credit_note_id: creditNoteId, reason: body?.reason }, customerOf(customerUserId)),
    keyed(idempotencyKey),
  )),
  discardDraft: async (orgId, creditNoteId, body, idempotencyKey, customerUserId) => typedAnswer(await api.post(
    messagesPath(orgId),
    envelope('credit_note.discard', {
      credit_note_id: creditNoteId,
      ...(body?.reason === undefined ? {} : { reason: body.reason }),
    }, customerOf(customerUserId)),
    keyed(idempotencyKey),
  )),
};
